import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './CreateMarketplaceObjectPage.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, ImagePreviewsGrid, BadWordsModal, BackButton, Button } from './shared';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import useLogout from '../hooks/useLogout';
import {
  popoverContainerStyle,
  popoverHeaderStyle,
  popoverMessageStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './shared/PopoverStyles';

/**
 * Issue #101: CreateMarketplaceObjectPage - Separate page for creating marketplace objects
 * Migrated from popover/modal to full page for better UX
 */
const CreateMarketplaceObjectPage = ({ onObjectCreated }) => {
  const navigation = useNavigation();
  const { t } = useLanguage();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);

  // Form state
  const [marketplaceType, setMarketplaceType] = useState('offer');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [price, setPrice] = useState('');
  const [isFreeGiveaway, setIsFreeGiveaway] = useState(false);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [userData, setUserData] = useState(null);
  // Issue #105: State for unsaved changes confirmation dialog
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  // Issue #161: State for bad words modal
  const [showBadWordsModal, setShowBadWordsModal] = useState(false);
  const [detectedBadWords, setDetectedBadWords] = useState([]);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/api/user/me');
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
      }
    };
    fetchUserData();
  }, []);

  // Issue #246, #262: Prevent auto-focus and ensure page starts at top
  useEffect(() => {
    // Scroll to top of page immediately
    window.scrollTo(0, 0);

    // Issue #262: Prevent any input from receiving focus during initial load
    // This catches browser auto-focus behavior that happens after React render
    const preventFocus = (e) => {
      const target = e.target;
      // Only prevent focus on input elements (text, textarea, select)
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        e.target.blur();
      }
    };

    // Listen for focus events during initial load period
    document.addEventListener('focusin', preventFocus, true);

    // Remove listener after page has fully loaded (300ms should be enough)
    const timer = setTimeout(() => {
      document.removeEventListener('focusin', preventFocus, true);
    }, 300);

    // Also blur any currently focused element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    return () => {
      document.removeEventListener('focusin', preventFocus, true);
      clearTimeout(timer);
    };
  }, []);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const descriptionRef = useRef(null);

  // Constants
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_IMAGE_DIMENSION = 1920;
  const COMPRESSION_QUALITY = 0.85;

  // Auto-resize description textarea
  useEffect(() => {
    const textarea = descriptionRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [content]);

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  reject(new Error(t('marketplace.imageTooLarge') || 'Bild ist zu groß'));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error(t('marketplace.errorCompressing') || 'Fehler beim Komprimieren'));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error(t('marketplace.errorLoading') || 'Fehler beim Laden'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error(t('marketplace.errorReading') || 'Fehler beim Lesen'));
      reader.readAsDataURL(file);
    });
  };

  // Handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    if (images.length + files.length > MAX_FILES) {
      setError((t('marketplace.maxImagesError') || 'Maximal {count} Bilder erlaubt').replace('{count}', MAX_FILES));
      return;
    }

    const validFiles = [];
    const newPreviews = [];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError((t('marketplace.invalidFileType') || 'Ungültiger Dateityp: {name}').replace('{name}', file.name));
        continue;
      }

      try {
        const compressedFile = await compressImage(file);
        validFiles.push(compressedFile);

        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            newPreviews.push(event.target.result);
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(compressedFile);
        });
      } catch (err) {
        console.error('Compression failed:', err);
        validFiles.push(file);
        try {
          await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
              newPreviews.push(event.target.result);
              resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        } catch (readerErr) {
          console.error('Preview failed:', readerErr);
        }
      }
    }

    setImages([...images, ...validFiles]);
    setImagePreviews([...imagePreviews, ...newPreviews]);
    e.target.value = '';
  };

  // Remove image at index
  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!title.trim()) {
      setError(t('marketplace.pleaseEnterTitle') || 'Bitte gib einen Titel ein');
      return;
    }

    if (!content.trim()) {
      setError(t('marketplace.pleaseEnterDescription') || 'Bitte gib eine Beschreibung ein');
      return;
    }

    if (marketplaceType === 'offer') {
      if (!isFreeGiveaway && (!price || parseFloat(price) <= 0)) {
        setError(t('marketplace.pleaseEnterValidPrice') || 'Bitte gib einen gültigen Preis ein');
        return;
      }
      if (images.length === 0) {
        setError(t('marketplace.pleaseUploadImage') || 'Bitte lade mindestens ein Bild hoch');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('post_type', 'marketplace');
      formData.append('marketplace_type', marketplaceType);
      formData.append('title', title);
      formData.append('content', content);

      if (marketplaceType === 'offer') {
        formData.append('is_free_giveaway', isFreeGiveaway ? 'true' : 'false');
      }

      if (price && !isFreeGiveaway) {
        formData.append('price', price);
      }

      images.forEach((file) => {
        formData.append('images', file);
      });

      const response = await axios.post('/api/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200) {
        setShowSuccess(true);
        if (onObjectCreated) {
          onObjectCreated(response.data);
        }
        // Issue #216: Save marketplace filter to localStorage so created object is visible
        localStorage.setItem('marketplaceFilter', marketplaceType);
        // Issue #221: Navigate to marketplace tab so user can see their new object
        localStorage.setItem('landingPageTab', 'market');
        // Auto-navigate after success message
        setTimeout(() => {
          navigation.onNavigateToLandingPage();
        }, 1500);
      }
    } catch (err) {
      console.error('Error creating marketplace object:', err);
      // Issue #161: Check for bad words in response
      if (err.response?.data?.bad_words && err.response.data.bad_words.length > 0) {
        setDetectedBadWords(err.response.data.bad_words);
        setShowBadWordsModal(true);
        return;
      }
      setError(err.response?.data?.message || (t('marketplace.errorCreating') || 'Fehler beim Erstellen'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Issue #105: Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return title.trim().length > 0 || content.trim().length > 0 || price.length > 0 || images.length > 0;
  };

  const handleBack = () => {
    // Issue #105: Show confirmation if there are unsaved changes
    if (hasUnsavedChanges()) {
      setShowLeaveConfirm(true);
    } else {
      navigation.onNavigateBack();
    }
  };

  // Issue #105: Confirm leave and navigate back
  const handleConfirmLeave = () => {
    setShowLeaveConfirm(false);
    navigation.onNavigateBack();
  };

  // Issue #105: Cancel leave and stay on page
  const handleCancelLeave = () => {
    setShowLeaveConfirm(false);
  };

  // Success overlay
  if (showSuccess) {
    return (
      <div className="create-marketplace-page">
        <div className="create-marketplace-success-overlay">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <p className="success-text">{t('marketplace.objectCreatedSuccess') || 'Objekt erfolgreich erstellt!'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-marketplace-page">
      <AppHeader 
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation with title */}
      <div className="tab-navigation-create-marketplace">
        <span className="tab-title">{t('marketplace.createObjectTitle') || 'Ein neues Objekt erstellen'}</span>
      </div>

      {/* Main Content */}
      <div className="create-marketplace-page-content">
        {/* Back Button - Issue #115: Added aria-label for accessibility */}
        <BackButton
          onClick={handleBack}
          className="back-button-create-marketplace"
        />

        {/* Error Message - Issue #115: Added role="alert" for screen reader announcement */}
        {error && <div className="create-marketplace-error-message" role="alert" aria-live="assertive">{error}</div>}

        <form onSubmit={handleSubmit} className="create-marketplace-page-form">
          {/* Type Toggle (Segmented Button) - Issue #115: Added aria-pressed for accessibility */}
          <div className="marketplace-type-toggle" role="group" aria-label={t('marketplace.selectType') || 'Anzeigentyp auswählen'}>
            <button
              type="button"
              className={`type-segment type-segment-start ${marketplaceType === 'offer' ? 'active' : ''}`}
              onClick={() => setMarketplaceType('offer')}
              aria-pressed={marketplaceType === 'offer'}
            >
              {t('tabs.iOffer') || 'Ich biete'}
            </button>
            <button
              type="button"
              className={`type-segment type-segment-end ${marketplaceType === 'search' ? 'active' : ''}`}
              onClick={() => setMarketplaceType('search')}
              aria-pressed={marketplaceType === 'search'}
            >
              {t('tabs.iSearch') || 'Ich suche'}
            </button>
          </div>

          {/* Photo Section - Issue #115: Improved accessibility */}
          <div className="marketplace-section">
            <span className="section-label" id="photo-section-label">{t('marketplace.photo') || 'Foto'}</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              id="marketplace-image-upload"
              aria-labelledby="photo-section-label"
            />
            <div
              className={`marketplace-image-upload-area${imagePreviews.length > 0 ? ' marketplace-image-upload-compact' : ''}`}
              onClick={() => document.getElementById('marketplace-image-upload').click()}
              role="button"
              tabIndex={0}
              aria-label={imagePreviews.length > 0
                ? (t('marketplace.addMoreImages') || 'Mehr Bilder hinzufügen')
                : marketplaceType === 'offer'
                  ? (t('marketplace.uploadImagesRequired') || 'Bilder hochladen (erforderlich)')
                  : (t('marketplace.uploadImagesOptional') || 'Bilder hochladen (optional)')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  document.getElementById('marketplace-image-upload').click();
                }
              }}
            >
              <div className="upload-placeholder">
                <span className="upload-icon" aria-hidden="true">+</span>
                <span className="upload-text">
                  {imagePreviews.length > 0
                    ? (t('marketplace.addMoreImages') || 'Weitere Bilder hinzufügen')
                    : marketplaceType === 'offer'
                      ? (t('marketplace.uploadImagesRequired') || 'Bilder hochladen (erforderlich)')
                      : (t('marketplace.uploadImagesOptional') || 'Bilder hochladen (optional)')}
                </span>
              </div>
            </div>

            {/* Image Previews - displayed under upload area */}
            {/* Issue #226: showPreviewBadge marks first image as preview */}
            <ImagePreviewsGrid
              previews={imagePreviews}
              onRemove={removeImage}
              showPreviewBadge={true}
            />
          </div>

          {/* Title Field - Issue #115: Added aria-label for accessibility */}
          <div className="marketplace-section">
            <label className="section-label" htmlFor="marketplace-title">{t('marketplace.titleLabel') || 'Titel'}</label>
            <input
              type="text"
              id="marketplace-title"
              className="marketplace-text-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={marketplaceType === 'offer'
                ? (t('marketplace.placeholderTitleOffer') || 'z.B. iPhone 12, gut erhalten')
                : (t('marketplace.placeholderTitleSearch') || 'z.B. Suche gebrauchten Laptop')}
              aria-required="true"
            />
          </div>

          {/* Description Field - Issue #115: Added aria-label for accessibility */}
          <div className="marketplace-section">
            <label className="section-label" htmlFor="marketplace-description">{t('marketplace.descriptionLabel') || 'Beschreibung'}</label>
            <textarea
              ref={descriptionRef}
              id="marketplace-description"
              className="marketplace-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={marketplaceType === 'offer'
                ? (t('marketplace.placeholderDescriptionOffer') || 'Beschreibe dein Angebot...')
                : (t('marketplace.placeholderDescriptionSearch') || 'Beschreibe was du suchst...')}
              aria-required="true"
            />
          </div>

          {/* Free Giveaway Checkbox (only for offers) - Issue #115: Added proper ARIA attributes */}
          {marketplaceType === 'offer' && (
            <div className="marketplace-checkbox-section">
              <label className="marketplace-checkbox-label">
                <input
                  type="checkbox"
                  checked={isFreeGiveaway}
                  onChange={() => setIsFreeGiveaway(!isFreeGiveaway)}
                  className="visually-hidden"
                  aria-label={t('marketplace.freeGiveaway') || 'Zum Verschenken'}
                />
                <span className={`checkbox-custom ${isFreeGiveaway ? 'checked' : ''}`} aria-hidden="true">
                  {isFreeGiveaway && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M1 4L3.5 6.5L9 1" stroke="#F5F5F5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span className="checkbox-text">{t('marketplace.freeGiveaway') || 'Zum Verschenken'}</span>
              </label>
            </div>
          )}

          {/* Price Field (required for offers unless free giveaway, optional for search) - Issue #115: Added aria-label */}
          <div className={`marketplace-section ${marketplaceType === 'offer' && isFreeGiveaway ? 'disabled' : ''}`}>
            <label className="section-label" htmlFor="marketplace-price">
              {marketplaceType === 'offer' ? (t('marketplace.priceLabel') || 'Preis *') : 'Preis (optional)'}
            </label>
            <div className="price-input-container">
              <input
                type="number"
                id="marketplace-price"
                className="marketplace-price-input"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={marketplaceType === 'offer' ? '0.00' : '0.00 (optional)'}
                step="0.01"
                min="0"
                disabled={marketplaceType === 'offer' && isFreeGiveaway}
                aria-required={marketplaceType === 'offer' && !isFreeGiveaway}
                aria-describedby="price-currency"
              />
              <span className="price-currency" id="price-currency" aria-hidden="true">EUR</span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="create-marketplace-submit-wrapper">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting || !title.trim() || !content.trim() || (marketplaceType === "offer" && (images.length === 0 || (!isFreeGiveaway && (!price || parseFloat(price) <= 0))))}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (t('marketplace.creating') || 'Erstelle...') : (t('marketplace.create') || 'Erstellen')}
            </Button>
          </div>
        </form>
      </div>

      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
      <BottomNavigation
        activeTab="create"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Issue #105: Unsaved changes confirmation modal - Issue #115: Added accessibility attributes */}
      {showLeaveConfirm && (
        <div
          className="modal-overlay"
          onClick={handleCancelLeave}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={popoverContainerStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mp-leave-confirm-title"
            aria-describedby="mp-leave-confirm-message"
          >
            <h2 style={popoverHeaderStyle} id="mp-leave-confirm-title">{t('unsavedChanges.title')}</h2>
            <p style={popoverMessageStyle} id="mp-leave-confirm-message">{t('unsavedChanges.message')}</p>
            <div style={popoverButtonContainerStyle}>
              <button
                onClick={handleCancelLeave}
                style={getCancelButtonStyle(false)}
              >
                {t('unsavedChanges.stay')}
              </button>
              <button
                onClick={handleConfirmLeave}
                style={getSubmitButtonStyle(false)}
              >
                {t('unsavedChanges.leave')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #161: Bad words modal */}
      <BadWordsModal
        isOpen={showBadWordsModal}
        onClose={() => setShowBadWordsModal(false)}
        badWords={detectedBadWords}
        contentType="object"
      />
    </div>
  );
};

export default CreateMarketplaceObjectPage;
